import LoadingBar from '@cloudscape-design/chat-components/loading-bar';
import {
  Box,
  Button,
  ColumnLayout,
  Container,
  ContainerProps,
  ContentLayout,
  Header,
  Link,
  Modal,
  SpaceBetween,
  Table,
} from '@cloudscape-design/components';
import { Timestamp } from '@firebase/firestore';
import { saveAs } from 'file-saver';
import chunk from 'lodash.chunk';
import { useEffect, useState } from 'react';
import { useLocalStorage } from 'react-use';
import * as XLSX from 'xlsx';
import { Agp, Loss } from '../api/agp';
import { fetchItemsByIds, fetchItemsWithNestedFilters, fetchItemsWithOrFilters, Filter } from '../api/generic';
import { createAgpPeriodFilter } from '../api/preMadeFilters';
import PeriodFilterControls, { ViewMode } from '../components/PeriodFilterControls';
import { useAuthContext } from '../contexts/AuthContext';
import { Batch } from '../models/Batch';
import { GraphLink } from '../models/GraphLink';
import { calculateEstimatedExciseDutyEUR } from '../utils/audit';
import {
  formatQuarterHeader,
  getEndOfQuarter,
  getEndOfYear,
  getStartOfCurrentQuarter,
  getStartOfQuarter,
  getStartOfYear,
} from '../utils/date';
import { prefixCollection } from '../utils/prefix';
import { toDate } from '../utils/timestamp';

export type OverviewData = {
  periodLabel: string;
  batchesStarted: number;
  batchesWorking: number;
  batchesEnded: number;
  batchesReleased: number;
  totalVolumeProducedLiters: number;
  batchesWithNoYield: number;
  linkedInvoiceCount: number;
  estimatedExciseDutyEUR: number;
  losses: {
    thrub: number;
    filtration: number;
    sampling: number;
  };
};

export type QuickOverview = {
  addedToAgp: number;
  removedFromAgp: number;
  releasedFromAgp: number;
  exciseAmount: number;
  totalLoss: number;
};

type StyledContainerProps = ContainerProps & { invalid?: boolean };

const StyledContainer = ({ invalid, ...props }: StyledContainerProps) => (
  <div style={invalid ? { border: '2px solid #d13212', borderRadius: '8px' } : undefined}>
    <Container {...props} />
  </div>
);

const Audit = () => {
  const { tenant, department } = useAuthContext();
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useLocalStorage('audit_qy', 'quarter');
  const [activeDate, setActiveDate] = useState<Date>(getStartOfCurrentQuarter());
  const [auditView, setAuditView] = useState<'production' | 'excise'>('production');
  const [exciseRows, setExciseRows] = useState<any[]>([]);

  const [ended, setEnded] = useState<Batch[]>([]);
  const [agp, setAgp] = useState<Agp[]>([]);

  const [quickOverview, setQuickOverview] = useState<QuickOverview>({
    addedToAgp: 0,
    removedFromAgp: 0,
    releasedFromAgp: 0,
    exciseAmount: 0,
    totalLoss: 0,
  });

  const [overviewIssues, setOverviewIssues] = useState<
    Partial<Record<keyof QuickOverview, { agps?: Agp[]; batches?: Batch[]; losses?: Loss[] }>>
  >({});
  const [errorField, setErrorField] = useState<keyof QuickOverview | null>(null);

  const toJSDate = (t: Timestamp | Date | undefined) => (!t ? undefined : t instanceof Date ? t : t.toDate());

  const inPeriod = (d: Date | undefined, from: Timestamp, to: Timestamp) => {
    if (!d) return false;
    const start = from.toDate();
    const end = to.toDate();
    return d >= start && d <= end;
  };

  const [overviewData, setOverviewData] = useState<OverviewData>({
    periodLabel: '',
    batchesStarted: 0,
    batchesWorking: 0,
    batchesEnded: 0,
    batchesReleased: 0,
    totalVolumeProducedLiters: 0,
    batchesWithNoYield: 0,
    linkedInvoiceCount: 0,
    estimatedExciseDutyEUR: 0,
    losses: {
      thrub: 0,
      filtration: 0,
      sampling: 0,
    },
  });

  const startDate = Timestamp.fromDate(
    viewMode === 'quarter'
      ? getStartOfQuarter(activeDate)
      : viewMode === 'year'
        ? getStartOfYear(activeDate)
        : new Date(activeDate.getFullYear(), activeDate.getMonth(), 1),
  );

  const endDate = Timestamp.fromDate(
    viewMode === 'quarter'
      ? getEndOfQuarter(activeDate)
      : viewMode === 'year'
        ? getEndOfYear(activeDate)
        : new Date(activeDate.getFullYear(), activeDate.getMonth() + 1, 0, 23, 59, 59, 999),
  );

  const fetchLoss = async (batchData: Batch[]) => {
    const safeChunkSize = 10; // 10 batch IDs = 10 OR filters

    const batchChunks = chunk(
      batchData.map((b) => b.id),
      safeChunkSize,
    );

    const allLosses: Loss[] = [];

    for (const batchIdChunk of batchChunks) {
      const filters: Filter<Loss>[] = batchIdChunk.map((id) => ({
        field: 'batchId',
        operator: '==',
        value: id,
      }));

      const lossesForChunk = await fetchItemsWithOrFilters<Loss>(prefixCollection('loss', tenant), { or: filters });

      allLosses.push(...lossesForChunk);
    }

    return allLosses;
  };

  const batchBottleGroups = ended.flatMap((batch) => {
    const batchAgps = agp.filter((a) => a.batchId === batch.id);

    // group by bottle size and accumulate IN/OUT/SOLD correctly
    const bySize = batchAgps.reduce((acc, a) => {
      const size = a.size ?? 0;
      if (!acc.has(size)) {
        acc.set(size, { batch, size, in: 0, out: 0, sold: 0 });
      }
      const g = acc.get(size)!;

      if (!a.releasedDate) {
        // IN
        g.in += a.amount ?? 0;
      } else {
        // OUT (+ optional sold part)
        g.out += a.amount ?? 0;
        g.sold += a.sold ?? 0; // NOTE: add the sold quantity, not the whole amount
      }

      return acc;
    }, new Map<number, { batch: Batch; size: number; in: number; out: number; sold: number }>());
    return [...bySize.values()].map((g) => {
      const unreleased = Math.max(0, g.in - g.out); // on hand
      const released = Math.max(0, g.out - g.sold); // released but not sold
      const initial = g.in; // equals IN

      return {
        batch: g.batch,
        size: g.size,
        initial,
        unreleased,
        released,
        sold: g.sold,
      };
    });
  });

  const fetchData = async () => {
    setLoading(true);

    const agpFilter = createAgpPeriodFilter<Agp>(startDate, endDate);
    let allAgps = await fetchItemsWithNestedFilters<Agp>(prefixCollection('agp', tenant), agpFilter);

    const batchIds = Array.from(new Set(allAgps.map((a) => a.batchId)));
    const fetchedBatches = await fetchItemsByIds<Batch>(prefixCollection('batches', tenant), batchIds);
    const batchData = fetchedBatches.filter((b) => b && (b.department === department || department === ''));

    const validBatchIds = new Set(batchData.map((b) => b.id));
    allAgps = allAgps.filter((a) => validBatchIds.has(a.batchId));

    if (allAgps.length === 0) {
      setOverviewData({
        periodLabel:
          viewMode === 'quarter'
            ? formatQuarterHeader(getStartOfQuarter(activeDate))
            : viewMode === 'month'
              ? `${activeDate.toLocaleString('default', { month: 'long' })} ${activeDate.getFullYear()}`
              : `Year ${activeDate.getFullYear()}`,
        batchesStarted: 0,
        batchesWorking: 0,
        batchesEnded: 0,
        batchesReleased: 0,
        totalVolumeProducedLiters: 0,
        batchesWithNoYield: 0,
        linkedInvoiceCount: 0,
        estimatedExciseDutyEUR: 0,
        losses: {
          thrub: 0,
          filtration: 0,
          sampling: 0,
        },
      });
      setQuickOverview({
        addedToAgp: 0,
        removedFromAgp: 0,
        releasedFromAgp: 0,
        exciseAmount: 0,
        totalLoss: 0,
      });
      setOverviewIssues({});
      setAgp([]);
      setEnded([]);
      setLoading(false);
      return;
    }

    setAgp(allAgps);

    // Continue with link fetching and calculation

    const batchesEnded = batchData.filter((b) => {
      const batchEndDate = toDate(b.endDate);
      return batchEndDate >= startDate.toDate() && batchEndDate <= endDate.toDate();
    });

    setEnded(batchesEnded);

    const lossesFromBatches = await fetchLoss(batchesEnded);
    const invalidLosses: Loss[] = [];
    const sumLosses = (type: Loss['type']) => {
      let total = 0;
      for (const loss of lossesFromBatches.filter((l) => l.type === type)) {
        if (typeof loss.amount !== 'number' || isNaN(loss.amount)) {
          invalidLosses.push(loss);
        } else {
          total += loss.amount;
        }
      }
      return total;
    };
    const thrubAmount = sumLosses('thrub');
    const filtrationAmount = sumLosses('filtration');
    const samplingAmount = sumLosses('sampling');

    const safeChunkSize = 15; // 15 batches => 30 filters
    const batchChunks = chunk(batchesEnded, safeChunkSize);

    const allLinks: GraphLink[] = [];

    for (const chunkedBatches of batchChunks) {
      const filters: Filter<GraphLink>[] = chunkedBatches.flatMap((batch) => [
        {
          field: 'from',
          operator: '==',
          value: batch.id,
        },
        {
          field: 'to',
          operator: '==',
          value: batch.id,
        },
      ]);

      const chunkLinks = await fetchItemsWithOrFilters<GraphLink>(prefixCollection('graphLinks', tenant), {
        or: filters,
      });

      allLinks.push(...chunkLinks);
    }

    const uniqueInvoiceIds = new Set<string>();

    for (const link of allLinks) {
      if (link.type.includes('invoice')) {
        if (link.type[0] === 'invoice') {
          uniqueInvoiceIds.add(link.from);
        } else if (link.type[1] === 'invoice') {
          uniqueInvoiceIds.add(link.to);
        }
      }
    }

    const uniqueInvoices = await fetchItemsByIds(prefixCollection('invoices', tenant), [...uniqueInvoiceIds]);

    const linkedInvoiceCount = uniqueInvoices.length;

    const batchesStarted = batchData.filter((b) => {
      const batchStartDate = toDate(b.startDate);
      if (b.name.includes('🇳🇴')) return false; // Skip test batches
      return batchStartDate >= startDate.toDate() && batchStartDate <= endDate.toDate();
    }).length;

    const batchesWorking = batchData.filter((b) => {
      const batchStartDate = toDate(b.startDate);
      const batchEndDate = toDate(b.endDate);
      if (b.name.includes('🇳🇴')) return false; // Skip test batches
      return (
        batchStartDate >= startDate.toDate() && batchStartDate <= endDate.toDate() && batchEndDate >= endDate.toDate()
      );
    }).length;

    // OUT rows with releasedDate inside the selected period
    const exciseOutThisPeriod = allAgps.filter(
      (a) => a.releasedDate && inPeriod(toJSDate(a.releasedDate), startDate, endDate),
    );

    // Optional: a tidy shape for a small table
    const exciseRows = exciseOutThisPeriod.map((a) => ({
      batch: batchData.find((b) => b.id === a.batchId)?.name ?? a.batchId,
      bottle: a.name ?? '—',
      size: a.size ?? 0,
      qty: a.amount ?? 0,
      releasedDate: toJSDate(a.releasedDate!)!,
      sold: a.sold ?? 0,
      invoice: a.salesInvoiceId ?? '—',
      note: a.note ?? '',
    }));

    // Store once so UI can read it
    // Add at the top: const [exciseRows, setExciseRows] = useState<any[]>([]);
    setExciseRows(exciseRows);

    const batchesReleased = new Set(
      allAgps
        .filter((a) => a.releasedDate && inPeriod(toJSDate(a.releasedDate), startDate, endDate))
        .map((a) => a.batchId),
    ).size;

    const totalVolumeProducedLiters = batchesEnded.reduce((sum, b) => {
      return sum + (b.yield || 0);
    }, 0);

    const batchesWithNoYield = batchesEnded.filter((b) => !b.yield).length;

    let estimatedExciseDutyEUR = calculateEstimatedExciseDutyEUR(batchesEnded);
    const invalidExciseBatches = batchesEnded.filter(
      (b) =>
        typeof b.yield !== 'number' || isNaN(b.yield ?? 0) || typeof b.abvFinal !== 'number' || isNaN(b.abvFinal ?? 0),
    );
    if (invalidExciseBatches.length || isNaN(estimatedExciseDutyEUR)) {
      estimatedExciseDutyEUR = NaN;
    }

    const losses = {
      thrub: thrubAmount,
      filtration: filtrationAmount,
      sampling: samplingAmount,
    };

    const periodLabel =
      viewMode === 'quarter'
        ? formatQuarterHeader(getStartOfQuarter(activeDate))
        : viewMode === 'month'
          ? `${activeDate.toLocaleString('default', { month: 'long' })} ${activeDate.getFullYear()}`
          : `Year ${activeDate.getFullYear()}`;

    setOverviewData({
      periodLabel,
      batchesStarted,
      batchesWorking,
      batchesEnded: batchesEnded.length,
      batchesReleased,
      totalVolumeProducedLiters,
      batchesWithNoYield,
      linkedInvoiceCount,
      estimatedExciseDutyEUR,
      losses,
    });

    const issues: Partial<Record<keyof QuickOverview, { agps?: Agp[]; batches?: Batch[]; losses?: Loss[] }>> = {};

    const addedAgps = allAgps.filter(
      (a) => !a.releasedDate && inPeriod(toJSDate(a.createdDate), startDate, endDate) && (a.amount ?? 0) > 0,
    );
    const invalidAddedAgps = addedAgps.filter((a) => typeof a.amount !== 'number' || isNaN(a.amount));
    const addedToAgp = invalidAddedAgps.length ? NaN : addedAgps.reduce((sum, a) => sum + (a.amount ?? 0), 0);
    if (invalidAddedAgps.length) issues.addedToAgp = { agps: invalidAddedAgps };

    const removedAgps = allAgps.filter(
      (a) => !a.releasedDate && inPeriod(toJSDate(a.createdDate), startDate, endDate) && (a.amount ?? 0) < 0,
    );
    const invalidRemovedAgps = removedAgps.filter((a) => typeof a.amount !== 'number' || isNaN(a.amount));
    const removedFromAgp = invalidRemovedAgps.length
      ? NaN
      : removedAgps.reduce((sum, a) => sum + Math.abs(a.amount ?? 0), 0);
    if (invalidRemovedAgps.length) issues.removedFromAgp = { agps: invalidRemovedAgps };

    const releasedAgps = allAgps.filter(
      (a) => a.releasedDate && inPeriod(toJSDate(a.releasedDate), startDate, endDate),
    );
    const invalidReleasedAgps = releasedAgps.filter((a) => typeof a.amount !== 'number' || isNaN(a.amount));
    const releasedFromAgp = invalidReleasedAgps.length
      ? NaN
      : releasedAgps.reduce((sum, a) => sum + (a.amount ?? 0), 0);
    if (invalidReleasedAgps.length) issues.releasedFromAgp = { agps: invalidReleasedAgps };

    const totalLoss = invalidLosses.length ? NaN : (thrubAmount + filtrationAmount + samplingAmount) / 1000;
    if (invalidLosses.length) issues.totalLoss = { losses: invalidLosses };

    if (invalidExciseBatches.length) {
      issues.exciseAmount = { batches: invalidExciseBatches };
    }

    setQuickOverview({
      addedToAgp,
      removedFromAgp,
      releasedFromAgp,
      exciseAmount: estimatedExciseDutyEUR,
      totalLoss,
    });
    setOverviewIssues(issues);

    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [viewMode, activeDate, tenant, department]);

  const fieldLabels: Record<keyof QuickOverview, string> = {
    addedToAgp: 'Added to AGP',
    removedFromAgp: 'Removed from AGP',
    releasedFromAgp: 'Released from AGP',
    exciseAmount: 'Excise Amount (€)',
    totalLoss: 'Total Loss (L)',
  };

  const handlePeriodChange = (direction: 'prev' | 'next') => {
    if (loading) return; // Prevent changes while loading
    setActiveDate((prev) => {
      const month = prev.getMonth();
      const year = prev.getFullYear();

      if (viewMode === 'quarter') {
        const newMonth = direction === 'prev' ? month - 3 : month + 3;
        return new Date(year, newMonth, 1);
      } else if (viewMode === 'month') {
        const newMonth = direction === 'prev' ? month - 1 : month + 1;
        return new Date(year, newMonth, 1);
      } else {
        const newYear = direction === 'prev' ? year - 1 : year + 1;
        return new Date(newYear, 0, 1);
      }
    });
  };

  const rollupForExport = (batches: Batch[], agps: Agp[]) => {
    const map = new Map<
      string,
      {
        batch: Batch;
        size: number;
        in: number;
        out: number;
        sold: number;
      }
    >();

    const getKey = (a: Agp) => `${a.batchId}::${a.size ?? 0}`;

    const batchMap = Object.fromEntries(batches.map((b) => [b.id, b]));

    for (const a of agps) {
      const k = getKey(a);
      if (!map.has(k)) {
        map.set(k, {
          batch: batchMap[a.batchId] ?? ({ id: a.batchId, name: a.batchId } as Batch),
          size: a.size ?? 0,
          in: 0,
          out: 0,
          sold: 0,
        });
      }
      const r = map.get(k)!;
      if (a.releasedDate) {
        r.out += a.amount ?? 0; // OUT rows
        r.sold += a.sold ?? 0; // sold is a subset of OUT
      } else {
        r.in += a.amount ?? 0; // IN rows
      }
    }

    return [...map.values()].map((r) => {
      const onHand = Math.max(0, r.in - r.out);
      const releasedNotSold = Math.max(0, r.out - r.sold);
      return { ...r, onHand, releasedNotSold, initial: r.in };
    });
  };

  const handleExportToExcel = () => {
    const wb = XLSX.utils.book_new();

    // === Overview Sheet ===
    const overviewSheet = XLSX.utils.json_to_sheet([
      {
        'Batches Started': overviewData.batchesStarted,
        'Batches in Progress': overviewData.batchesWorking,
        'Batches Ended': overviewData.batchesEnded,
        'Batches Released': overviewData.batchesReleased,
        'Total Volume Produced (L)': overviewData.totalVolumeProducedLiters,
        'Linked Invoices': overviewData.linkedInvoiceCount,
        'Estimated Excise Duty (€)': overviewData.estimatedExciseDutyEUR,
      },
    ]);
    XLSX.utils.book_append_sheet(wb, overviewSheet, 'Overview');

    // === Losses Sheet ===
    const lossesSheet = XLSX.utils.json_to_sheet([
      {
        'Thrub Losses (L)': overviewData.losses.thrub / 1000,
        'Filtration Losses (L)': overviewData.losses.filtration / 1000,
        'Sampling Losses (L)': overviewData.losses.sampling / 1000,
      },
    ]);
    XLSX.utils.book_append_sheet(wb, lossesSheet, 'Losses');

    // === Batches Sheet (ledger-driven) ===
    const rollups = rollupForExport(ended, agp);

    const batchSheet = XLSX.utils.json_to_sheet(
      rollups.map((r) => ({
        'Batch Name': r.batch.name,
        'Batch Number': r.batch.batchId ?? '—',
        'Bottle Size (L)': r.size,
        'Total Bottles (Initial)': r.initial, // = IN
        'Unreleased (On Hand)': r.onHand, // = IN - OUT
        'Released (Not Sold)': r.releasedNotSold, // = OUT - SOLD
        Sold: r.sold, // = sum sold on OUT rows
        'Yield (L)': r.batch.yield ?? 0,
        'Total Excise Paid (€)': r.batch.exciseDuty?.exciseDutyAmount?.toFixed(2) ?? '—',
        'Excise Payment Reference': r.batch.exciseDuty?.exciseDutyPaymentReferenceId ?? '—',
      })),
    );
    XLSX.utils.book_append_sheet(wb, batchSheet, 'Batches');

    // === AGP (raw rows; mark IN vs OUT) ===
    const agpRows = agp.map((a) => ({
      Batch: ended.find((b) => b.id === a.batchId)?.name ?? a.batchId,
      Bottle: a.name ?? 'Unnamed',
      'Size (L)': a.size ?? 0,
      Amount: a.amount ?? 0,
      Kind: a.releasedDate ? 'OUT' : 'IN',
      'Created Date': toDate(a.createdDate).toLocaleDateString(),
      'Released Date': a.releasedDate ? toDate(a.releasedDate).toLocaleDateString() : '',
      Sold: a.sold ?? 0,
      'Sales Invoice': a.salesInvoiceId ?? '',
      Note: a.note ?? '',
    }));
    const agpSheet = XLSX.utils.json_to_sheet(agpRows);
    XLSX.utils.book_append_sheet(wb, agpSheet, 'AGP');

    // === AGP Flow (pretty view) ===
    const agpFlowSheet = XLSX.utils.json_to_sheet(
      agpRows.map((x) => ({
        'Batch Name': x.Batch,
        Bottle: x.Bottle,
        'Size (L)': x['Size (L)'],
        Qty: x.Amount,
        Kind: x.Kind, // IN or OUT
        'Effective Date': x['Released Date'] || x['Created Date'],
        Sold: x.Sold,
        Invoice: x['Sales Invoice'],
        Note: x.Note,
      })),
    );
    XLSX.utils.book_append_sheet(wb, agpFlowSheet, 'AGP Flow');

    // === Releases (OUT only) ===
    const releaseRows = agp
      .filter((a) => a.releasedDate)
      .map((a) => ({
        Batch: ended.find((b) => b.id === a.batchId)?.name ?? a.batchId,
        Bottle: a.name ?? 'Unnamed',
        'Size (L)': a.size ?? 0,
        'Qty Released': a.amount ?? 0,
        'Released Date': toDate(a.releasedDate!).toLocaleDateString(),
        'Sold (of Released)': a.sold ?? 0,
        'Sales Invoice': a.salesInvoiceId ?? '',
        Note: a.note ?? '',
      }));

    const releaseSheet = XLSX.utils.json_to_sheet(releaseRows);
    XLSX.utils.book_append_sheet(wb, releaseSheet, 'Releases');

    // === Paid Excise Sheet ===
    const excisePaidSheet = XLSX.utils.json_to_sheet(
      ended
        .filter((batch) => !!batch.exciseDuty?.exciseDutyAmount)
        .map((batch) => ({
          'Batch Name': batch.name,
          'Batch Number': batch.batchId ?? '—',
          'Excise Paid (€)': batch.exciseDuty?.exciseDutyAmount?.toFixed(2) ?? '—',
          'Date Paid': batch.exciseDuty?.exciseDutyPaid
            ? toDate(batch.exciseDuty.exciseDutyPaid).toLocaleDateString()
            : '—',
          'Payment Reference': batch.exciseDuty?.exciseDutyPaymentReferenceId ?? '—',
        })),
    );

    XLSX.utils.book_append_sheet(wb, excisePaidSheet, 'Paid Excise');

    // === Save File ===
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: 'application/octet-stream' });
    const filename = `Audit_Report_${overviewData.periodLabel.replace(/\s/g, '_')}.xlsx`;
    saveAs(blob, filename);
  };

  return (
    <ContentLayout
      disableOverlap
      header={
        <Header
          variant="h1"
          description="This report provides a traceable overview of all batches and their linked invoices for customs review"
          actions={
            <SpaceBetween size="xs" direction="horizontal">
              <Button
                variant={auditView === 'production' ? 'primary' : 'normal'}
                onClick={() => setAuditView('production')}
              >
                Production
              </Button>
              <Button variant={auditView === 'excise' ? 'primary' : 'normal'} onClick={() => setAuditView('excise')}>
                Excise
              </Button>
              <Button iconName="grid-view" onClick={handleExportToExcel}>
                Export to Excel
              </Button>
            </SpaceBetween>
          }
        >
          Audit tracker
        </Header>
      }
    >
      <SpaceBetween size="l" direction="vertical">
        <PeriodFilterControls
          viewMode={viewMode as ViewMode}
          setViewMode={(mode) => setViewMode(mode)}
          activeDate={activeDate}
          handlePeriodChange={handlePeriodChange}
          loading={loading}
        />

        <Box variant="p">
          Show report for{' '}
          <b>
            {viewMode === 'quarter'
              ? formatQuarterHeader(getStartOfQuarter(activeDate))
              : viewMode === 'month'
                ? `${activeDate.toLocaleString('default', {
                    month: 'long',
                  })} ${activeDate.getFullYear()}`
                : `Year ${activeDate.getFullYear()}`}
          </b>
        </Box>
        <div style={{ height: '4px' }}>
          {loading && (
            // useLoadingBar
            <LoadingBar variant="gen-ai" />
          )}
        </div>
        <SpaceBetween size="m" direction="vertical">
          <Header variant="h2">Overview</Header>
          <ColumnLayout columns={3} variant="text-grid">
            <StyledContainer
              header={<Header variant="h3">Added to AGP</Header>}
              invalid={isNaN(quickOverview.addedToAgp)}
              footer={
                isNaN(quickOverview.addedToAgp) && (
                  <Button onClick={() => setErrorField('addedToAgp')}>View details</Button>
                )
              }
            >
              {isNaN(quickOverview.addedToAgp) ? '—' : quickOverview.addedToAgp}
            </StyledContainer>
            <StyledContainer
              header={<Header variant="h3">Removed from AGP</Header>}
              invalid={isNaN(quickOverview.removedFromAgp)}
              footer={
                isNaN(quickOverview.removedFromAgp) && (
                  <Button onClick={() => setErrorField('removedFromAgp')}>View details</Button>
                )
              }
            >
              {isNaN(quickOverview.removedFromAgp) ? '—' : quickOverview.removedFromAgp}
            </StyledContainer>
            <StyledContainer
              header={<Header variant="h3">Released from AGP</Header>}
              invalid={isNaN(quickOverview.releasedFromAgp)}
              footer={
                isNaN(quickOverview.releasedFromAgp) && (
                  <Button onClick={() => setErrorField('releasedFromAgp')}>View details</Button>
                )
              }
            >
              {isNaN(quickOverview.releasedFromAgp) ? '—' : quickOverview.releasedFromAgp}
            </StyledContainer>
            <StyledContainer
              header={<Header variant="h3">Excise Amount (€)</Header>}
              invalid={isNaN(quickOverview.exciseAmount)}
              footer={
                isNaN(quickOverview.exciseAmount) && (
                  <Button onClick={() => setErrorField('exciseAmount')}>View details</Button>
                )
              }
            >
              {isNaN(quickOverview.exciseAmount) ? '—' : `€${quickOverview.exciseAmount.toFixed(2)}`}
            </StyledContainer>
            <StyledContainer
              header={<Header variant="h3">Total Loss (L)</Header>}
              invalid={isNaN(quickOverview.totalLoss)}
              footer={
                isNaN(quickOverview.totalLoss) && (
                  <Button onClick={() => setErrorField('totalLoss')}>View details</Button>
                )
              }
            >
              {isNaN(quickOverview.totalLoss) ? '—' : `${quickOverview.totalLoss}L`}
            </StyledContainer>
          </ColumnLayout>

          {errorField && (
            <Modal
              visible
              onDismiss={() => setErrorField(null)}
              closeAriaLabel="Close dialog"
              header={`Invalid data for ${fieldLabels[errorField]}`}
            >
              <SpaceBetween size="s" direction="vertical">
                {overviewIssues[errorField]?.agps && (
                  <Box variant="p">
                    <b>AGPs</b>
                    <ul>
                      {overviewIssues[errorField]!.agps!.map((a) => (
                        <li key={a.id ?? `${a.batchId}-${toJSDate(a.createdDate)?.toISOString()}`}>
                          Batch <Link href={`/batches/${a.batchId}`}>{a.batchId}</Link> amount: {String(a.amount)}
                        </li>
                      ))}
                    </ul>
                  </Box>
                )}
                {overviewIssues[errorField]?.batches && (
                  <Box variant="p">
                    <b>Batches</b>
                    <ul>
                      {overviewIssues[errorField]!.batches!.map((b) => (
                        <li key={b.id}>
                          <Link href={`/batches/${b.id}`}>{b.name}</Link>
                        </li>
                      ))}
                    </ul>
                  </Box>
                )}
                {overviewIssues[errorField]?.losses && (
                  <Box variant="p">
                    <b>Losses</b>
                    <ul>
                      {overviewIssues[errorField]!.losses!.map((l, i) => (
                        <li key={i}>
                          Batch <Link href={`/batches/${l.batchId}`}>{l.batchId}</Link> amount: {String(l.amount)}
                        </li>
                      ))}
                    </ul>
                  </Box>
                )}
              </SpaceBetween>
            </Modal>
          )}

          {auditView === 'excise' && (
            <>
              <Header variant="h2">Excise Releases (in period)</Header>
              <Table
                stickyHeader
                variant="embedded"
                trackBy={(r) => `${r.batch}-${r.bottle}-${r.size}-${r.releasedDate?.toISOString?.()}`}
                columnDefinitions={[
                  { id: 'date', header: 'Released Date', cell: (r) => r.releasedDate.toLocaleDateString() },
                  { id: 'batch', header: 'Batch', cell: (r) => r.batch },
                  { id: 'bottle', header: 'Bottle', cell: (r) => r.bottle },
                  { id: 'size', header: 'Size (L)', cell: (r) => r.size },
                  { id: 'qty', header: 'Qty', cell: (r) => r.qty },
                  { id: 'sold', header: 'Sold (of Qty)', cell: (r) => r.sold },
                  { id: 'invoice', header: 'Sales Invoice', cell: (r) => r.invoice },
                  { id: 'note', header: 'Note', cell: (r) => r.note },
                ]}
                items={exciseRows}
                empty={<Box variant="p">No releases recorded in this period.</Box>}
              />
            </>
          )}

          {auditView === 'production' && (
            <>
              <Header variant="h2">Batches</Header>
              <Table
                columnDefinitions={[
                  {
                    id: 'batch',
                    header: 'Batch Name',
                    cell: (item) => <Link href={`/batches/${item.batch.id}`}>{item.batch.name}</Link>,
                  },
                  {
                    id: 'size',
                    header: 'Bottle Size (L)',
                    cell: (item) => `${item.size}L`,
                  },
                  {
                    id: 'initial',
                    header: 'Total Bottles (Initial)',
                    cell: (item) => item.initial,
                  },
                  {
                    id: 'unreleased',
                    header: 'Unreleased',
                    cell: (item) => item.unreleased,
                  },
                  {
                    id: 'released',
                    header: 'Released (Ready for Sale)',
                    cell: (item) => item.released,
                  },
                  {
                    id: 'sold',
                    header: 'Sold',
                    cell: (item) => item.sold,
                  },
                  {
                    id: 'balanceCheck',
                    header: 'Balance Check',
                    cell: (item) => {
                      const sum = item.unreleased + item.released + item.sold;
                      return sum === item.initial ? '✅ OK' : `⚠️ ${item.initial - sum} mismatch`;
                    },
                  },
                ]}
                items={batchBottleGroups}
                trackBy={(item) => `${item.batch.id}-${item.size}`}
                stickyHeader
                variant="embedded"
              />
            </>
          )}
        </SpaceBetween>
      </SpaceBetween>
    </ContentLayout>
  );
};

export default Audit;
